// ======================================================================
// 📁 File: src/modules/ticket/TicketLayout.jsx
// 📝 Purpose: Ticket Module Layout (Sidebar + Content)
//        • Hides ONLY the immediate shell/page header above Ticket module
//        • Does NOT modify global CSS files
//        • Reverts header visibility when leaving Ticket module
// 🕒 Updated: 2025-12-01 13:35 IST
// ======================================================================

import React, { useEffect, useState } from "react";
import TicketSidebar from "./layout/TicketSidebar";

/**
 * Helper: Safe fetch of CSS variable
 */
function getCssVar(varName, fallback = null) {
  try {
    const val = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return val || fallback;
  } catch (e) {
    return fallback;
  }
}

export default function TicketLayout({ children }) {
  
  // -------------------------------------------------------------------
  // ⭐ 1. HIDE ONLY THE IMMEDIATE PAGE HEADER ABOVE TICKET MODULE
  // -------------------------------------------------------------------
  useEffect(() => {
    // Try detecting common header wrappers (only inside content shell)
    const possibleHeaders = [
      ".page-header",
      ".content-header",
      ".module-header",
      ".top-header",
      ".header-wrapper",
      ".page-title-wrapper",
    ];

    let foundHeader = null;

    for (const selector of possibleHeaders) {
      const el = document.querySelector(selector);
      if (el) {
        foundHeader = el;
        break;
      }
    }

    if (foundHeader) {
      foundHeader.dataset.originalDisplay = foundHeader.style.display || "";
      foundHeader.style.display = "none";
    }

    // Restore on unmount
    return () => {
      if (foundHeader) {
        foundHeader.style.display = foundHeader.dataset.originalDisplay;
      }
    };
  }, []);

  // -------------------------------------------------------------------
  // ⭐ 2. SIDEBAR COLLAPSE CONTROL
  // -------------------------------------------------------------------
  const collapsedLS = localStorage.getItem("ticket_sidebar_collapsed");
  const initialExpanded = collapsedLS === null ? true : collapsedLS === "false";

  const [sidebarExpanded, setSidebarExpanded] = useState(initialExpanded);

  useEffect(() => {
    const handler = () => {
      const collapsed =
        localStorage.getItem("ticket_sidebar_collapsed") === "true";
      setSidebarExpanded(!collapsed);
    };
    window.addEventListener("ticket_sidebar_update", handler);
    return () =>
      window.removeEventListener("ticket_sidebar_update", handler);
  }, []);

  const handleSidebarToggle = (isExpanded) => {
    setSidebarExpanded(Boolean(isExpanded));
    localStorage.setItem("ticket_sidebar_collapsed", isExpanded ? "false" : "true");
    window.dispatchEvent(new Event("ticket_sidebar_update"));
  };

  // -------------------------------------------------------------------
  // ⭐ 3. MAIN CONTENT AREA STYLE
  // -------------------------------------------------------------------
  const expandedWidth = getCssVar("--tf-sidebar-w", "260px");
  const collapsedWidth = getCssVar("--tf-sidebar-w-collapsed", "64px");

  const contentStyle = {
    transition: "margin-left 0.22s ease, width 0.22s ease",
    marginLeft: sidebarExpanded ? expandedWidth : collapsedWidth,
    padding: "18px",
    minHeight: "100vh",
    background: "#faf7fb",
    boxSizing: "border-box",
  };

  return (
    <div className="ticket-layout-wrapper" style={{ minHeight: "100vh" }}>
      <TicketSidebar onToggle={handleSidebarToggle} />

      <main className="ticket-layout-content" style={contentStyle}>
        {children}
      </main>
    </div>
  );
}
